import { useEffect, useState } from "react";

import { message, Spin } from "antd";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import commonservice from "@apis/commonservice";
import { useInChinaMainland } from "@app/kalodata";
import { useModalTip } from "@components/ModalAutoAlert";
import { CLIENT_KEY, CLIENT_KEY_STAGING, LOCALSTORAGE_KEY } from "@config";
import { useShowLoginLimitModal } from "@pages/home/login/LoginLimitModal/hooks";
import report from "@report";

import { useAppSelector } from "./redux";
import { useSavedUrlParams } from "./useSavedUrlParams";

export const useTiktokAuth = (
  type = "",
  successFn?: (data: any, fn?: any) => {},
  failFn = (_err?: string) => {},
  authCb?: () => {},
) => {
  const [loading, setLoading] = useState<boolean>(false);
  const inChinaMainland = useInChinaMainland();
  const { t } = useTranslation();
  const { setShowLoginLimitModal, setLoginLimitNumber } = useShowLoginLimitModal();

  const { userInfo } = useAppSelector((state) => state.globalReducer);
  const errorModalTip = useModalTip({ type: "error", title: "You have canceled." });
  const userModalTip = useModalTip({ type: "info", title: "Verified successfully" });
  const successModalTip = useModalTip({
    type: "confirm",
    title: userInfo.subAccount ? "Verified successfully" : "Congratulations on getting 1-day standard membership!",
    subtitle: "You can also log in with this TikTok account.",
  });
  const { membership } = useAppSelector((state) => state.globalReducer);
  const navigage = useNavigate();
  const { saveUrlParams, restoreSavedUrlParams, clearSavedParams } = useSavedUrlParams();

  // tiktok 授权回调
  useEffect(() => {
    if (!type) return;
    const searchParams = new URLSearchParams(location.search);
    // ?error=access_denied&error_description=User%
    const error = searchParams.get("error") || "";
    const code = searchParams.get("code") || "";
    const loginMethod = searchParams.get("loginMethod") || "";
    if (code && loginMethod === "tiktok") {
      setLoading(true);
      report.event("tiktok_callback", {
        kalo_pos: "tiktok_callback_count",
        kalo_type: type,
      });
      if (type === "login" || type === "signup") {
        commonservice
          .tiktokLogin({ code, type })
          .then((data: any) => {
            // 登录成功后保存 token
            if (data.accessToken) {
              localStorage.setItem(LOCALSTORAGE_KEY.ACCESS_TOKEN, data.accessToken);
            }
            successFn &&
              successFn?.(data, (userInfo: any) => {
                // 设置登录类型是tiktok
                localStorage.setItem(LOCALSTORAGE_KEY.LOGIN_TYPE, "tiktok");
                sessionStorage.setItem("showTikTokInfo", "true");
                setLoading(false);
                // report.event("tiktok_callback_success", {
                //   kalo_pos: "tiktok_callback_count",
                //   kalo_type: type,
                //   kalo_username: userInfo.username || userInfo.tiktokUserHandle,
                // });
                report.event("link_success", {
                  kalo_type: "tt_" + type,
                  kalo_username: userInfo.username || userInfo.tiktokUserHandle,
                });
              });
          })
          .catch((err) => {
            // report.event("tiktok_callback_error", {
            //   kalo_pos: "tiktok_callback_count",
            //   kalo_type: type,
            //   kalo_value: err,
            // });
            report.event("link_error", {
              kalo_type: "tt_" + type,
              kalo_json: {
                err,
              },
            });
            setLoading(false);
            if (err.code === "1303") {
              setLoginLimitNumber(err.message);
              setShowLoginLimitModal(true);
            } else {
              if (err.code == "1110") {
                err.message = t("The TikTok account is linked to another account. You can try another TikTok account.");
              }
              if (err.code == "1106") {
                err.message = t('Oops, something seems to be wrong, please "re-authorize".');
              }
              errorModalTip.open({
                title: "Verified unsuccessfully",
                subtitle: err.message,
              });
            }
            failFn && failFn(err);
          })
          .finally(() => {
            navigage(restoreSavedUrlParams());
            clearSavedParams();
          });
      } else if (type === "authorize") {
        // 调用授权接口
        commonservice
          .verifyAccount({
            loginMethod: "TIKTOK",
            code,
          })
          .then((_res: any) => {
            // report.event("tiktok_callback_success", {
            //   kalo_pos: "tiktok_callback_count",
            //   kalo_type: type,
            // });
            report.event("link_success", {
              kalo_type: "tt_" + type,
            });
            const level = membership?.level === "Trial" ? "Standard" : membership?.level;
            successModalTip.open({
              type: "confirm",
              title: userInfo.subAccount
                ? "Verified successfully"
                : `Congratulations on getting 1-day ${level?.toLocaleLowerCase()} membership!`,
              subtitle: "You can also log in with this TikTok account.",
            });
            authCb && authCb();
          })
          .catch((err) => {
            // report.event("tiktok_callback_error", {
            //   kalo_pos: "tiktok_callback_count",
            //   kalo_type: type,
            //   kalo_value: err,
            // });
            report.event("link_error", {
              kalo_type: "tt_" + type,
              kalo_json: {
                err,
              },
            });
            try {
              if (err.code === "1303") {
                setLoginLimitNumber(err.message);
                setShowLoginLimitModal(true);
              } else {
                if (err.code == "1110") {
                  err.message = t(
                    "The TikTok account is linked to another account. You can try another TikTok account.",
                  );
                }
                if (err.code == "1106") {
                  err.message = t('Oops, something seems to be wrong, please "re-authorize".');
                }
                errorModalTip.open({
                  title: "Verified unsuccessfully",
                  subtitle: err.message,
                });
              }

              failFn && failFn(err);
            } catch (error) {
              console.log(error, "error");
            }
          })
          .finally(() => {
            navigage(restoreSavedUrlParams());
            clearSavedParams();
            setLoading(false);
          });
      }
    }
    if (error === "access_denied") {
      report.event("tiktok_callback", {
        kalo_pos: "tiktok_callback_count",
        kalo_type: type,
      });
      // report.event("tiktok_callback_error", {
      //   kalo_pos: "tiktok_callback_count",
      //   kalo_type: type,
      //   kalo_value: "You have canceled",
      // });
      report.event("link_error", {
        kalo_type: "tt_" + type,
        kalo_value: "You have canceled",
      });
      errorModalTip.open({ type: "error", title: "You have canceled." });
      navigage(restoreSavedUrlParams());
      clearSavedParams();
    }
  }, []);

  // 授权回来loading 等待
  const SpinDom = () => {
    return (
      <div className="spin">
        {loading && (
          <div className="tiktok_loading">
            <Spin tip={t("Loading") + "..."}></Spin>
          </div>
        )}
        {errorModalTip.showDom()}
        {userModalTip.showDom()}
        {successModalTip.showDom()}
      </div>
    );
  };

  // 点击按钮 登录
  const tiktokLink = (type: "login" | "signup" | "me") => {
    if (inChinaMainland) return message.info(t("The current IP does not support TikTok authorization"));
    setLoading(true);

    // 保存当前 URL 参数（排除 TikTok 相关的参数）
    saveUrlParams();

    // 根据环境动态拼接重定向 URI
    const isStaging = window.location.hostname.includes("staging");
    const domain = isStaging ? "staging.kalodata.com" : "www.kalodata.com";
    const SERVER_ENDPOINT_REDIRECT = `https://${domain}/tiktok/`;

    let url = "https://www.tiktok.com/v2/auth/authorize/";
    url += `?client_key=${isStaging ? CLIENT_KEY_STAGING : CLIENT_KEY}`;
    url += "&scope=user.info.basic";
    url += "&response_type=code";
    url += `&redirect_uri=${encodeURIComponent(SERVER_ENDPOINT_REDIRECT)}`;
    url += "&state=" + type;
    window.open(url, "_self");
    report.click({
      kalo_pos: "click_tiktok_link",
      kalo_type: type,
    });
  };

  return {
    tiktokLink,
    SpinDom,
  };
};
